import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { Group, Label, Tag, Text as KonvaText, Rect } from 'react-konva';
import type Konva from 'konva';
import type { LayoutElement } from '../../types';
import { getOhtDockPosition } from '../../utils/topology';
import { getOhtPivotWorld } from '../../utils/ohtPivot';
import { useSimulationVehicles, type FrameVehicle } from '../../hooks/useSimulationFrames';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectElement } from '../../features/selection/selectionSlice';
import { selectInstance } from '../../features/equipment/equipmentSlice';
import { selectLayoutPort, selectTrackNode } from '../../features/layout/layoutSlice';
import { getPlaybackPosition } from '../../utils/simulationPlayback';
import { resolveOhtCarriedFoupVisibleFromRuntime } from '../../features/simulation/ohtCarriedFoupVisible';
import {
  indexApiLegsByOht,
  pickActiveApiLeg,
  ohtTip,
  ohtTipChipColors,
  phaseChipClass,
  phaseChipColors,
  shortEndpointLabel,
} from '../../utils/simulationPhaseUi';
import { simulationClockStore } from '../../store/simulationClockStore';
import { replaySnapshotStore } from '../../store/replaySnapshotStore';
import { playbackDelayStore } from '../../store/playbackDelayStore';
import { simulationFrameBus } from '../../store/simulationFrameBus';
import { railTransferPoseStore } from '../../store/railTransferPoseStore';
import { transferWallHoldStore } from '../../store/transferWallHoldStore';
import { traceOhtPlaybackPosition } from '../../utils/ohtPlaybackDebug';
import { liveOhtRuntimeStore } from '../../store/liveOhtRuntimeStore';
import { liveMotionStore } from '../../store/liveMotionStore';
import { Oht2DShape } from './Amhs2dShapes';
import {
  mapSimPhaseToOhtStatus,
  resolveOhtStatusColor,
  uprightOht2DRotation,
} from './amhs/Oht2D';
import type { Oht2DFoupKind } from './amhs/Oht2D';

/** Tips / status lights are context, not motion — update below the paint rate. */
const TIP_UPDATE_INTERVAL_MS = 100;

type VehicleNodeRefs = {
  root: Konva.Group | null;
  labels: Konva.Group | null;
  phaseLabel: Konva.Label | null;
  phaseTag: Konva.Tag | null;
  phaseText: Konva.Text | null;
  /** Resolved once on mount so tip updates never walk the OHT subtree. */
  statusLights: Konva.Node[];
};

type SimulatedVehicleProps = {
  v: FrameVehicle;
  zoomLevel: number;
  registerRefs: (id: string, refs: VehicleNodeRefs | null) => void;
  onSelect: (layoutId: string) => void;
};

function centerKonvaLabel(label: Konva.Label | null): void {
  if (!label) return;
  label.offsetX(label.width() / 2);
}

const subscribeLiveRuntime = (notify: () => void) =>
  liveOhtRuntimeStore.subscribe(() => notify());

const SimulatedVehicleView: React.FC<SimulatedVehicleProps> = ({
  v,
  zoomLevel,
  registerRefs,
  onSelect,
}) => {
  const source = useAppSelector((state) => state.simulation.source);
  const showOhtLabels = useAppSelector((state) => state.ui.showOhtLabels ?? false);
  const [isHovered, setIsHovered] = useState(false);
  const rootRef = useRef<Konva.Group | null>(null);
  const labelsRef = useRef<Konva.Group | null>(null);
  const phaseLabelRef = useRef<Konva.Label | null>(null);
  const phaseTagRef = useRef<Konva.Tag | null>(null);
  const phaseTextRef = useRef<Konva.Text | null>(null);
  const idleChip = phaseChipColors('idle');
  // One compact snapshot per OHT keeps React's external-store equality check
  // primitive and avoids two subscriptions/callbacks for every animation frame.
  // -1 = replay, bit 0 = loaded, bit 1 = transferring.
  const liveVisualState = useSyncExternalStore(
    subscribeLiveRuntime,
    () => {
      if (source !== 'live') return -1;
      const runtime = liveOhtRuntimeStore.getOht(v.ohtId);
      const loaded = resolveOhtCarriedFoupVisibleFromRuntime(runtime);
      const transferring = Boolean(runtime?.transfer || runtime?.gateTransfer);
      return (loaded ? 1 : 0) | (transferring ? 2 : 0);
    },
    () => -1
  );
  // The scheduler classifies the carrier at pickup; an exit-gate handoff is
  // processed by definition and covers replays that carry no kind at all.
  const foupKind = useSyncExternalStore(
    subscribeLiveRuntime,
    (): Oht2DFoupKind => {
      if (source !== 'live') return 'raw';
      const runtime = liveOhtRuntimeStore.getOht(v.ohtId);
      return (
        runtime?.carriedFoupKind ??
        (runtime?.gateTransfer?.role === 'exit' ? 'processed' : 'raw')
      );
    },
    (): Oht2DFoupKind => 'raw'
  );

  const isLoaded =
    liveVisualState < 0 ? v.isLoaded === true : Boolean(liveVisualState & 1);
  const liveTransferring = liveVisualState >= 0 && Boolean(liveVisualState & 2);
  const ohtStatus = mapSimPhaseToOhtStatus(v.phaseChip, {
    transferring: liveTransferring || Boolean(v.transferPortId),
  });

  useLayoutEffect(() => {
    // Stream identity owns animation refs. `v.id` is the layout selection id
    // and aliases can make two backend OHTs resolve to the same layout element.
    registerRefs(v.ohtId, {
      root: rootRef.current,
      labels: labelsRef.current,
      phaseLabel: phaseLabelRef.current,
      phaseTag: phaseTagRef.current,
      phaseText: phaseTextRef.current,
      statusLights: rootRef.current?.find('.oht-status-light') ?? [],
    });
    return () => registerRefs(v.ohtId, null);
    // `isLoaded` swaps the Oht2D sprite subtree, so the status-light nodes
    // registered above are replaced and must be looked up again.
  }, [registerRefs, v.ohtId, isLoaded, zoomLevel]);

  return (
    <Group
      ref={rootRef}
      // Position/rotation are owned by the RAF loop — omit controlled props
      // so React re-renders (e.g. load sprite swap) do not fight the clock.
      offsetX={v.pivotX}
      offsetY={v.pivotY}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        e.cancelBubble = true;
        onSelect(v.id);
      }}
      onTap={(e) => {
        e.cancelBubble = true;
        onSelect(v.id);
      }}
      listening={true}
    >
      {/* Footprint hit target — Oht2D nested shapes are non-listening. */}
      <Rect width={v.w} height={v.h} fill="transparent" listening perfectDrawEnabled={false} />
      <Oht2DShape
        w={v.w}
        h={v.h}
        loaded={isLoaded}
        foupKind={foupKind}
        name={v.name}
        status={ohtStatus}
        hovered={isHovered}
        zoomLevel={zoomLevel}
        stroke="#26354A"
        strokeWidth={0.5}
        listening={false}
      />

      <Group ref={labelsRef} listening={false}>
        {/* Pick From / Drop To / Deadhead for — hidden while Idle (updated in RAF). */}
        <Label
          ref={phaseLabelRef}
          x={v.pivotX}
          y={v.h + 2 / zoomLevel}
          listening={false}
          visible={false}
        >
          <Tag ref={phaseTagRef} fill={idleChip.bg} cornerRadius={3 / zoomLevel} />
          <KonvaText
            ref={phaseTextRef}
            text=""
            fill={idleChip.fg}
            fontSize={9 / zoomLevel}
            padding={3 / zoomLevel}
            fontStyle="bold"
          />
        </Label>

        { (isHovered || showOhtLabels) && v.name && (
          <Label x={v.pivotX} y={-10 / zoomLevel} listening={false}>
            <Tag
              fill="rgba(17, 24, 39, 0.9)"
              pointerDirection="down"
              pointerWidth={8 / zoomLevel}
              pointerHeight={6 / zoomLevel}
              cornerRadius={4 / zoomLevel}
            />
            <KonvaText
              text={v.name}
              fill="white"
              fontSize={11 / zoomLevel}
              padding={6 / zoomLevel}
              align="center"
            />
          </Label>
        )}
      </Group>
    </Group>
  );
};

const SimulatedVehicle = React.memo(
  SimulatedVehicleView,
  (previous, next) => {
    const a = previous.v;
    const b = next.v;
    return (
      previous.zoomLevel === next.zoomLevel &&
      previous.registerRefs === next.registerRefs &&
      previous.onSelect === next.onSelect &&
      a.id === b.id &&
      a.ohtId === b.ohtId &&
      a.name === b.name &&
      a.w === b.w &&
      a.h === b.h &&
      a.pivotX === b.pivotX &&
      a.pivotY === b.pivotY &&
      a.isLoaded === b.isLoaded &&
      a.phaseChip === b.phaseChip &&
      a.transferPortId === b.transferPortId
    );
  }
);

const EMPTY_IDLE_MAP: Record<string, any> = {};
const EMPTY_PLAYBACK: Record<string, any> = {};
const EMPTY_API_LEGS: any[] = [];

type SimulationOverlayProps = {
  zoomLevel?: number;
  opacity?: number;
  /** Skip Konva paint while panning/zooming to keep navigation smooth. */
  navigationPreview?: boolean;
};

const SimulationOverlay: React.FC<SimulationOverlayProps> = ({
  zoomLevel = 1,
  navigationPreview = false,
}) => {
  const dispatch = useAppDispatch();
  const running = useAppSelector((state) => state.simulation.running);
  const source = useAppSelector((state) => state.simulation.source);
  // 2D Canvas stays mounted (CSS-hidden) under the 3D view — keep the paint
  // bus off while hidden so Konva never batchDraws into a 0×0 stage.
  const view3dOpen = useAppSelector((state) => state.ui.view3dOpen);
  const showOhtRouteLabels = useAppSelector(
    (state) => state.ui.showOhtRouteLabels ?? true
  );
  // Live motion is owned by liveMotionStore — selecting these from Redux on
  // every flush was re-rendering this overlay (and remounting Konva OHTs).
  const legacyPlaybackVehicles = useAppSelector((state) =>
    state.simulation.source === 'live' ? EMPTY_PLAYBACK : state.simulation.playbackVehicles
  );
  const legacyIdleByVehicle = useAppSelector((state) =>
    state.simulation.source === 'live'
      ? EMPTY_IDLE_MAP
      : (state.simulation.browserKinetic?.vehicleLegs.idleByVehicle ?? EMPTY_IDLE_MAP)
  );
  const legacyApiLegs = useAppSelector((state) =>
    state.simulation.source === 'live'
      ? EMPTY_API_LEGS
      : (state.simulation.browserKinetic?.apiLegs ?? EMPTY_API_LEGS)
  );
  const elements = useAppSelector((state) => state.layout.elements);
  const stockerPortOwners = useAppSelector(
    (state) => state.simulation.browserKinetic?.stockerPortOwners
  );
  const toolPortOwners = useAppSelector(
    (state) => state.simulation.browserKinetic?.toolPortOwners
  );
  const liveGates = useAppSelector((state) => state.simulation.live.inventory?.gates);
  const transportRequests = useAppSelector((state) => state.simulation.transportRequests);
  const frames = useSimulationVehicles();

  /**
   * World point an OHT parks on to work a port: the layout dock for stocker and
   * tool ports, the streamed coordinates for entry/exit gates. Mirrors the 3D
   * overlay so both views hold a transfer at the same place.
   */
  const transferPointByPortId = useMemo(() => {
    const byOwnerId = new Map<string, LayoutElement>();
    const ownerByPortId = new Map<string, LayoutElement>();
    for (const element of Object.values(elements)) {
      for (const ownerId of [
        element.id,
        element.metadata.name,
        String(element.customProperties?.toolId ?? ''),
        String(element.customProperties?.amhsId ?? ''),
        String(element.customProperties?.dxfEquipmentId ?? ''),
      ]) {
        if (ownerId) byOwnerId.set(ownerId, element);
      }
      const ports = element.customProperties?.ports;
      if (!Array.isArray(ports)) continue;
      for (const port of ports as Array<{ id?: string }>) {
        if (port.id) ownerByPortId.set(port.id, element);
      }
    }
    for (const [portId, ownerId] of Object.entries({
      ...(stockerPortOwners ?? {}),
      ...(toolPortOwners ?? {}),
    })) {
      const owner = byOwnerId.get(ownerId);
      if (owner) ownerByPortId.set(portId, owner);
    }

    const points = new Map<string, { x: number; y: number }>();
    for (const [portId, owner] of ownerByPortId) {
      const dock = getOhtDockPosition(owner, portId);
      if (dock) points.set(portId, { x: dock.x, y: dock.y });
    }
    for (const gate of liveGates ?? []) {
      if (points.has(gate.portId)) continue;
      if (
        typeof gate.x === 'number' &&
        Number.isFinite(gate.x) &&
        typeof gate.y === 'number' &&
        Number.isFinite(gate.y)
      ) {
        points.set(gate.portId, { x: gate.x, y: gate.y });
      }
    }
    return points;
  }, [elements, stockerPortOwners, toolPortOwners, liveGates]);

  const ohtLayoutPoseById = useMemo(() => {
    const poses = new Map<string, { x: number; y: number; angle: number }>();
    for (const element of Object.values(elements)) {
      if (element.type !== 'oht-vehicle') continue;
      // The editor rotates the vehicle about its top-left, so the pivot must
      // be rotated with it to land where the editor draws the body.
      const pivot = getOhtPivotWorld(element);
      const pose = { x: pivot.x, y: pivot.y, angle: element.rotation };
      const keys = [
        element.id,
        element.metadata.name,
        element.customProperties?.ohtId,
        element.customProperties?.amhsId,
      ];
      for (const key of keys) {
        if (typeof key === 'string' && key.length > 0 && !poses.has(key)) {
          poses.set(key, pose);
        }
      }
    }
    return poses;
  }, [elements]);

  const endpointNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const element of Object.values(elements)) {
      const ownerName =
        element.metadata.name?.trim() ||
        String(element.customProperties?.amhsId ?? '').trim() ||
        '';
      if (!ownerName) continue;
      const compact = ownerName.slice(0, 24);
      for (const key of [
        element.id,
        String(element.customProperties?.amhsId ?? ''),
        String(element.customProperties?.toolId ?? ''),
        String(element.customProperties?.dxfEquipmentId ?? ''),
        element.metadata.name,
      ]) {
        if (key) names[key] = compact;
      }
      const ports = element.customProperties?.ports;
      if (!Array.isArray(ports)) continue;
      for (const port of ports as Array<{ id?: string; name?: string }>) {
        if (!port.id) continue;
        names[port.id] = (port.name?.trim() || compact).slice(0, 24);
      }
    }
    return names;
  }, [elements]);

  /** Pickup / drop station names per transport request — never a track-node id. */
  const requestStationsById = useMemo(() => {
    const stations: Record<string, { pickup: string; drop: string }> = {};
    for (const request of transportRequests) {
      const pickup = shortEndpointLabel(
        request.pickupPort || request.pickupStocker || request.pickupToolId || request.pickup,
        endpointNames
      );
      const drop = shortEndpointLabel(
        request.dropPort ||
          request.destinationStocker ||
          request.dropToolId ||
          request.drop,
        endpointNames
      );
      for (const id of [request.transportRequestId, request.requestId]) {
        if (id) stations[id] = { pickup, drop };
      }
    }
    return stations;
  }, [endpointNames, transportRequests]);

  const nodeRefs = useRef(new Map<string, VehicleNodeRefs>());
  const ohtHasMovedRef = useRef(new Map<string, boolean>());
  const ohtLayoutPoseRef = useRef(ohtLayoutPoseById);
  ohtLayoutPoseRef.current = ohtLayoutPoseById;

  useEffect(() => {
    if (!running) ohtHasMovedRef.current.clear();
  }, [running]);

  const registerRefs = React.useCallback((id: string, refs: VehicleNodeRefs | null) => {
    if (refs) nodeRefs.current.set(id, refs);
    else nodeRefs.current.delete(id);
  }, []);

  const handleSelectOht = React.useCallback(
    (layoutId: string) => {
      dispatch(selectInstance(null));
      dispatch(selectLayoutPort(null));
      dispatch(selectTrackNode(null));
      dispatch(selectElement(layoutId));
    },
    [dispatch]
  );

  // Keep a mutable snapshot of frame metadata for the RAF loop without
  // re-subscribing the loop when React rebuilds the frames array.
  const framesRef = useRef(frames);
  framesRef.current = frames;
  const playbackRef = useRef(legacyPlaybackVehicles);
  const idleRef = useRef(legacyIdleByVehicle);
  const apiLegsRef = useRef(legacyApiLegs);
  const legsByOhtCacheRef = useRef<{
    apiLegs: typeof legacyApiLegs;
    index: ReturnType<typeof indexApiLegsByOht>;
  } | null>(null);

  // Live: pull motion from the store into refs so React never re-renders on
  // leg flushes. Legacy: keep the Redux mirrors in the refs.
  useEffect(() => {
    if (source !== 'live') {
      playbackRef.current = legacyPlaybackVehicles;
      idleRef.current = legacyIdleByVehicle;
      apiLegsRef.current = legacyApiLegs;
      return;
    }
    const pull = () => {
      const motion = liveMotionStore.getSnapshot();
      playbackRef.current = motion.playbackVehicles;
      idleRef.current = motion.idleByVehicle;
      apiLegsRef.current = motion.apiLegs;
    };
    pull();
    return liveMotionStore.subscribe(pull);
  }, [source, legacyPlaybackVehicles, legacyIdleByVehicle, legacyApiLegs]);

  const transferPointsRef = useRef(transferPointByPortId);
  useEffect(() => {
    transferPointsRef.current = transferPointByPortId;
  }, [transferPointByPortId]);
  const endpointNamesRef = useRef(endpointNames);
  endpointNamesRef.current = endpointNames;
  const requestStationsRef = useRef(requestStationsById);
  requestStationsRef.current = requestStationsById;
  const showOhtRouteLabelsRef = useRef(showOhtRouteLabels);
  showOhtRouteLabelsRef.current = showOhtRouteLabels;

  const navigationPreviewRef = useRef(navigationPreview);
  navigationPreviewRef.current = navigationPreview;

  useEffect(() => {
    if (!running || view3dOpen) return;

    const lastTipByOht = new Map<string, string>();
    let lastTipUpdateMs = 0;

    const applyTransforms = (t: number) => {
      if (navigationPreviewRef.current) return;

      const layer = nodeRefs.current.values().next().value?.root?.getLayer?.() ?? null;
      const stage = layer?.getStage?.() ?? null;
      const stageW = stage?.width?.() ?? 0;
      const stageH = stage?.height?.() ?? 0;
      // Skip draws while the stage has no drawable surface (hidden / mid-layout).
      if (!stage || stageW <= 0 || stageH <= 0) return;

      // Frame-invariant reads, hoisted so they cost once per frame instead of
      // once per vehicle: the replay snapshot and wall clock are shared by all
      // OHTs, and the leg index is keyed off the whole apiLegs array.
      const isLive = source === 'live';
      const replaySnapshot = isLive ? null : replaySnapshotStore.getSnapshot();
      const wallNowMs = isLive ? 0 : performance.now();
      const apiLegs = apiLegsRef.current;
      let legsCache = legsByOhtCacheRef.current;
      if (!legsCache || legsCache.apiLegs !== apiLegs) {
        legsCache = { apiLegs, index: indexApiLegsByOht(apiLegs) };
        legsByOhtCacheRef.current = legsCache;
      }
      const legsByOht = legsCache.index;
      const nowMs = performance.now();
      const updateTips =
        lastTipUpdateMs === 0 || nowMs - lastTipUpdateMs >= TIP_UPDATE_INTERVAL_MS;
      if (updateTips) lastTipUpdateMs = nowMs;

      for (const frame of framesRef.current) {
        const refs = nodeRefs.current.get(frame.ohtId);
        if (!refs?.root) continue;

        const playback = playbackRef.current[frame.ohtId];
        const idle = idleRef.current[frame.ohtId];

        const runtime = isLive ? liveOhtRuntimeStore.getOht(frame.ohtId) : null;
        let x = runtime?.x ?? frame.x;
        let y = frame.y;
        let angle = frame.angle;
        let scheduleMoving = false;
        let effectiveT = playbackDelayStore.getEffectiveTime(frame.ohtId, t);
        if (runtime) {
          if (runtime.state === 'MOVING') {
            ohtHasMovedRef.current.set(frame.ohtId, true);
          }
          const layoutPose =
            ohtLayoutPoseRef.current.get(frame.ohtId) ??
            ohtLayoutPoseRef.current.get(frame.id);
          const deferLayout =
            isLive &&
            layoutPose &&
            !ohtHasMovedRef.current.get(frame.ohtId) &&
            runtime.state !== 'MOVING' &&
            !runtime.transfer &&
            !runtime.gateTransfer;
          if (deferLayout) {
            x = layoutPose.x;
            y = layoutPose.y;
            angle = layoutPose.angle;
          } else {
            x = runtime.x;
            y = runtime.y;
            angle = runtime.angle;
          }
          effectiveT = runtime.localSimTimeS;
          scheduleMoving = runtime.state === 'MOVING';
        } else if (playback && idle) {
          const position = getPlaybackPosition(playback, idle, effectiveT, frame.idleAngle);
          x = position.x;
          y = position.y;
          angle = position.angle;
          scheduleMoving =
            position.leg != null &&
            (position.leg.fromX !== position.leg.toX ||
              position.leg.fromY !== position.leg.toY);
          traceOhtPlaybackPosition(frame.ohtId, effectiveT, position);
        }

        // Ride the turntable diameter bar / H-connector solid while transferring.
        const override =
          railTransferPoseStore.get(frame.ohtId) ?? railTransferPoseStore.get(frame.id);
        // Streamed equipment transfers own the stopped OHT heading. Ignore a
        // stale junction/connector pose while the pickup/drop is in progress.
        if (override && !runtime?.transfer && !runtime?.gateTransfer) {
          x = override.x;
          y = override.y;
          angle = override.angle;
        }

        // A streamed PICKUP/DROP can pin the vehicle to the backend node while
        // the DES handover window is active. Live playback follows the legs.
        const activeTransfer = replaySnapshot?.ohts[frame.ohtId]?.activeTransfer;
        // Armed on arrival, started when this vehicle's clock reaches the handoff.
        const wallHold = isLive
          ? null
          : transferWallHoldStore.get(frame.ohtId, wallNowMs, effectiveT);
        // Freeze this vehicle's clock for the rest of the pin, so the route it
        // resumes is the one continuing from the port.
        if (!isLive) {
          transferWallHoldStore.pinTimeline(
            wallHold,
            t,
            effectiveT,
            scheduleMoving
          );
          playbackDelayStore.settle(
            frame.ohtId,
            t,
            !wallHold && !scheduleMoving
          );
        }
        // `activeTransfer` is timed off the shared clock. While this vehicle
        // runs behind it, that transfer is one it has not reached, and pinning
        // to its port would teleport the vehicle forward down the route.
        const onSharedClock = playbackDelayStore.getDelaySec(frame.ohtId) <= 0;
        const handoffX = wallHold?.x ?? activeTransfer?.handoffX;
        const handoffY = wallHold?.y ?? activeTransfer?.handoffY;
        const transferPortId = wallHold?.portId ?? activeTransfer?.portId;
        const knownPoint =
          typeof handoffX === 'number' &&
          Number.isFinite(handoffX) &&
          typeof handoffY === 'number' &&
          Number.isFinite(handoffY)
            ? { x: handoffX, y: handoffY }
            : (transferPortId
                ? transferPointsRef.current.get(transferPortId)
                : undefined) ?? null;
        // A wall hold parks the vehicle even when the event names no node and
        // no dock resolves — otherwise the movement leg underneath keeps
        // running and the OHT slides past the port mid-handoff.
        const holdPoint =
          transferWallHoldStore.holdPoint(wallHold, knownPoint, { x, y }) ?? knownPoint;
        if (
          (wallHold ||
            (onSharedClock &&
              (activeTransfer?.action === 'PICKUP' ||
                activeTransfer?.action === 'DROP'))) &&
          holdPoint
        ) {
          x = holdPoint.x;
          y = holdPoint.y;
        }

        // Exact segment lerp each frame (HTML player style — no EMA lag).
        refs.root.position({ x, y });
        const screenAngle = uprightOht2DRotation(angle);
        refs.root.rotation(screenAngle);
        if (refs.labels) refs.labels.rotation(-screenAngle);

        // Tips / status lights are context — keep them off the 60Hz pose path.
        if (!updateTips) continue;

        const apiLeg = pickActiveApiLeg(legsByOht.get(frame.ohtId), effectiveT);
        const gateTransfer = runtime?.gateTransfer;
        const isVehicleLoaded = isLive
          ? resolveOhtCarriedFoupVisibleFromRuntime(runtime)
          : frame.isLoaded === true;
        // A replay `activeTransfer` also covers plain moves, which say nothing
        // about a handoff and must not read as a pick or a drop.
        const replayAction =
          activeTransfer?.action === 'PICKUP' || activeTransfer?.action === 'DROP'
            ? activeTransfer.action
            : null;
        const transferAction =
          runtime?.transfer?.action ??
          wallHold?.action ??
          replayAction ??
          (gateTransfer ? (gateTransfer.role === 'exit' ? 'DROP' : 'PICKUP') : null);
        const stations = apiLeg?.requestId
          ? requestStationsRef.current[apiLeg.requestId]
          : undefined;
        const pickupFrom =
          stations?.pickup ||
          shortEndpointLabel(apiLeg?.fromPortId, endpointNamesRef.current);
        const dropTo =
          stations?.drop ||
          shortEndpointLabel(apiLeg?.toPortId, endpointNamesRef.current);
        const { label, chip } = ohtTip(apiLeg, {
          transferAction,
          loaded: isVehicleLoaded,
          pickupFrom,
          dropTo,
        });
        const showRouteLabels = showOhtRouteLabelsRef.current;
        const tipKey = `${label}|${chip}|${showRouteLabels ? '1' : '0'}`;
        if (lastTipByOht.get(frame.ohtId) !== tipKey) {
          lastTipByOht.set(frame.ohtId, tipKey);
          const showTip = showRouteLabels && label !== 'Idle';
          refs.phaseLabel?.visible(showTip);
          if (showTip) {
            const chipColors = ohtTipChipColors(chip);
            refs.phaseText?.text(label);
            refs.phaseText?.fill(chipColors.fg);
            refs.phaseTag?.fill(chipColors.bg);
            centerKonvaLabel(refs.phaseLabel);
          }
          const status = mapSimPhaseToOhtStatus(phaseChipClass(apiLeg), {
            transferring: Boolean(runtime?.transfer ?? frame.transferPortId),
          });
          const statusFill = resolveOhtStatusColor(status);
          const lights = refs.statusLights;
          for (let i = 0; i < lights.length; i++) {
            const light = lights[i];
            if (light && 'fill' in light && typeof light.fill === 'function') {
              light.fill(statusFill);
            }
          }
        }
      }

      try {
        // Only redraw the dedicated simulation Layer (not floorplan / layout).
        layer?.batchDraw();
      } catch {
        // Konva can throw InvalidStateError if a buffer canvas is still 0×0
        // for a frame after un-hiding the 2D stage — skip that frame.
      }
    };

    // Same rAF as the clock: transfers already ran in the 'transfers' phase.
    const unsubscribe = simulationFrameBus.subscribe((simTime) => {
      applyTransforms(simTime);
    }, 'paint');

    applyTransforms(simulationClockStore.getTime());

    return () => {
      unsubscribe();
    };
  }, [running, source, view3dOpen]);

  if (!running || view3dOpen) return null;

  return (
    <Group listening={true}>
      {frames.map((v) => (
        <SimulatedVehicle
          key={`sim-oht-${v.ohtId}`}
          v={v}
          zoomLevel={zoomLevel}
          registerRefs={registerRefs}
          onSelect={handleSelectOht}
        />
      ))}
    </Group>
  );
};

export default React.memo(SimulationOverlay);
